import { useEffect } from 'react';
import {
    cleanupAll,
    clearWorkspaceSession,
    cssEscape,
    escapeAttribute,
    escapeHtml,
    formFilters,
    formatBytes,
    getJson,
    installSavedFilters,
    listen,
    loadWorkspaceResume,
    loadWorkspaceSession,
    openActionSheetFromDataset,
    postJson,
    saveWorkspaceSession,
} from './services.js';

export function useScreenController(routeKey) {
    useEffect(() => {
        const controller = {
            workspace: initWorkspace,
            more: initMore,
            history: initHistory,
            queue: initQueue,
            vault: initVault,
            collections: initCollections,
            research: initResearch,
            organize: initOrganize,
            admin: initAdmin,
            settings: initSettings,
            account: initAccount,
            reset: initResetPassword,
            inspector: initInspector,
        }[routeKey];

        return controller?.() || undefined;
    }, [routeKey]);
}

function initMore() {
    const adminTile = document.querySelector('[data-owner-admin-tile]');
    if (!adminTile) {
        return () => {};
    }

    let cancelled = false;
    adminTile.hidden = true;

    void getJson('/api/auth-state.php', { redirectAuth: false }).then((state) => {
        if (!cancelled) {
            adminTile.hidden = !(state.authenticated && state.user?.role === 'owner');
        }
    }).catch(() => {
        if (!cancelled) {
            adminTile.hidden = true;
        }
    });

    return () => {
        cancelled = true;
    };
}

function initAdmin() {
    const refresh = document.querySelector('#refresh-admin');
    const systemStatus = document.querySelector('#admin-system-status');
    const jobsStatus = document.querySelector('#admin-jobs-status');
    const queueStatus = document.querySelector('#admin-queue-status');
    const storageStatus = document.querySelector('#admin-storage-status');
    const failedForm = document.querySelector('#admin-failed-form');
    const failedStatus = document.querySelector('#admin-failed-status');
    const failedList = document.querySelector('#admin-failed-list');
    const queueOverviewStatus = document.querySelector('#admin-queue-overview-status');
    const queueList = document.querySelector('#admin-queue-list');
    const auditStatus = document.querySelector('#admin-audit-status');
    const auditList = document.querySelector('#admin-audit-list');

    if (!refresh || !systemStatus) {
        return () => {};
    }

    const formatCounts = (counts = {}) => Object.entries(counts)
        .map(([label, total]) => `${label}: ${total}`)
        .join(', ') || 'none';
    const storageBytes = (storage = {}) => Object.values(storage).reduce((sum, item) => sum + Number(item?.size_bytes || 0), 0);

    const loadSummary = async () => {
        systemStatus.textContent = 'Loading admin summary...';
        try {
            const summary = await getJson('/api/admin-summary.php');
            const counts = summary.counts || {};
            systemStatus.textContent = `${counts.jobs || 0} jobs, ${counts.active_sessions || 0} active sessions, ${counts.archive_exports || 0} archive exports.`;
            jobsStatus.textContent = `Status: ${formatCounts(summary.jobs?.by_status)}. Platforms: ${formatCounts(summary.jobs?.by_platform)}.`;
            queueStatus.textContent = `Status: ${formatCounts(summary.queue?.by_status)}. Platforms: ${formatCounts(summary.queue?.by_platform)}.`;
            storageStatus.textContent = `${formatBytes(storageBytes(summary.storage))} across managed storage. Last cleanup: ${summary.cleanup?.last_run?.finished_at || summary.cleanup?.last_run?.started_at || 'never'}.`;
        } catch (error) {
            systemStatus.textContent = error.message;
        }
    };
    const loadFailed = async () => {
        failedStatus.textContent = 'Loading failed jobs...';
        failedList.innerHTML = '';
        try {
            const data = await getJson(`/api/admin-failed-jobs.php?${new URLSearchParams(new FormData(failedForm)).toString()}`);
            failedStatus.textContent = `${data.items.length} failed job${data.items.length === 1 ? '' : 's'} shown.`;
            failedList.innerHTML = data.items.length ? data.items.map(renderAdminFailedJob).join('') : '<article class="history-card empty-state"><p>No failed jobs match this view.</p></article>';
        } catch (error) {
            failedStatus.textContent = error.message;
        }
    };
    const loadQueue = async () => {
        queueOverviewStatus.textContent = 'Loading queue overview...';
        queueList.innerHTML = '';
        try {
            const data = await getJson('/api/admin-queue-overview.php');
            queueOverviewStatus.textContent = `Queue status: ${formatCounts(data.counts?.by_status)}.`;
            queueList.innerHTML = data.items.length ? data.items.map(renderAdminQueueItem).join('') : '<article class="history-card empty-state"><p>No queued, processing, or failed queue items.</p></article>';
        } catch (error) {
            queueOverviewStatus.textContent = error.message;
        }
    };
    const loadAudit = async () => {
        auditStatus.textContent = 'Loading audit events...';
        auditList.innerHTML = '';
        try {
            const data = await getJson('/api/admin-audit-events.php');
            auditStatus.textContent = `${data.items.length} event${data.items.length === 1 ? '' : 's'} shown.`;
            auditList.innerHTML = data.items.length ? data.items.map(renderAdminAuditEvent).join('') : '<article class="history-card empty-state"><p>No audit events recorded yet.</p></article>';
        } catch (error) {
            auditStatus.textContent = error.message;
        }
    };
    const loadAll = async () => {
        await Promise.all([loadSummary(), loadFailed(), loadQueue(), loadAudit()]);
    };

    void loadAll();

    return cleanupAll([
        listen(refresh, 'click', loadAll),
        listen(failedForm, 'submit', (event) => {
            event.preventDefault();
            void loadFailed();
        }),
    ]);
}

function initWorkspace() {
    const form = document.querySelector('#download-form');
    const urlInput = document.querySelector('#media-url');
    const detectionCard = document.querySelector('#detection-card');
    const detectionText = document.querySelector('#detection-text');
    const jobCard = document.querySelector('#job-card');
    const jobText = document.querySelector('#job-text');
    const progressBar = document.querySelector('#progress-bar');
    const resultCard = document.querySelector('#result-card');
    const resultContent = document.querySelector('#result-content');
    const resumeStatus = document.querySelector('#resume-status');
    const resumeContent = document.querySelector('#resume-content');
    const processNextResume = document.querySelector('#process-next-resume');
    const clearResume = document.querySelector('#clear-resume');
    const pasteButton = document.querySelector('#paste-clipboard');
    const mobilePasteButton = document.querySelector('#mobile-paste');
    const mobileAnalyzeButton = document.querySelector('#mobile-analyze');
    const mobilePasteAnalyzeButton = document.querySelector('#mobile-paste-analyze');
    const installButton = document.querySelector('#install-saveflow');
    const pwaOnboardingStatus = document.querySelector('#pwa-onboarding-status');
    const notificationButton = document.querySelector('#enable-notifications');
    const notificationStatus = document.querySelector('#notification-status');
    const displayModeQuery = window.matchMedia?.('(display-mode: standalone)');

    if (!form || !urlInput) {
        return () => {};
    }

    const sharedUrl = extractSharedUrl(window.location.search);
    let lastSubmittedUrl = sharedUrl || localStorage.getItem('saveflow:lastUrl') || '';
    let lastJobId = null;
    let lastJobResult = null;
    let lastNotifiedJobId = null;
    let cancelled = false;

    const setDetection = (message, ok) => {
        detectionText.textContent = message;
        detectionCard.classList.toggle('is-ok', ok === true);
        detectionCard.classList.toggle('is-error', ok === false);
    };
    const setJobState = (message, progress, error = false) => {
        jobText.textContent = message;
        progressBar.style.width = `${Math.max(0, Math.min(100, Number(progress) || 0))}%`;
        jobCard.classList.toggle('is-error', error);
        jobCard.classList.toggle('is-ok', !error && progress >= 100);
    };
    const persist = (extra = {}) => saveWorkspaceSession({
        active_page: 'workspace',
        draft_url: urlInput.value.trim(),
        last_job_id: lastJobId,
        last_result: lastJobResult,
        ...extra,
    });
    const isInstalled = () => window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
    const setBusy = (busy) => {
        form.querySelector('button[type="submit"]').disabled = busy;
        urlInput.disabled = busy;
    };
    const resetResult = () => {
        resultCard.classList.remove('is-ok', 'is-error');
        resultContent.innerHTML = '<p>Downloads, duplicate notices, and file details will appear here after processing.</p>';
    };
    const pollJob = async (id) => {
        for (let attempt = 0; attempt < 8 && !cancelled; attempt += 1) {
            const status = await getJson(`/api/jobs-status.php?id=${encodeURIComponent(id)}`);
            lastJobId = status.id;
            lastJobResult = status;
            void persist();
            setJobState(status.message, status.progress, status.status === 'failed');

            if (status.status === 'completed') {
                showWorkspaceResult(status, resultCard, resultContent, lastSubmittedUrl);
                notifyJobComplete(status);
                return;
            }

            if (status.status === 'failed') {
                throw new Error(status.error?.message || 'The job failed.');
            }

            await new Promise((resolve) => window.setTimeout(resolve, 1200));
        }

        throw new Error('The job is still running. Refresh the status in a moment.');
    };
    const resumeLastJob = async (id) => {
        if (!id) {
            return;
        }

        try {
            setJobState('Restoring last job...', 10);
            const status = await getJson(`/api/jobs-status.php?id=${encodeURIComponent(id)}`);
            if (cancelled) {
                return;
            }
            lastJobId = status.id;
            lastJobResult = status;
            void persist();
            setJobState(status.message, status.progress, status.status === 'failed');

            if (status.status === 'completed') {
                showWorkspaceResult(status, resultCard, resultContent, lastSubmittedUrl);
                return;
            }

            if (status.status === 'failed') {
                showWorkspaceError(new Error(status.error?.message || status.message || 'The last job failed.'), resultCard, resultContent, form, lastSubmittedUrl);
                return;
            }

            await pollJob(status.id);
        } catch (error) {
            setJobState(error.message, 100, true);
        }
    };
    const submit = async (event) => {
        event?.preventDefault();
        const url = urlInput.value.trim();
        lastSubmittedUrl = url;
        localStorage.setItem('saveflow:lastUrl', url);
        void persist();

        if (!url) {
            setDetection('Paste a URL first.', false);
            return;
        }

        setBusy(true);
        resetResult();
        setDetection('Checking platform support...', null);
        setJobState('Preparing job...', 8);

        try {
            const detection = await postJson('/api/detect.php', { url });
            setDetection(`${detection.label} detected. Starting download job.`, true);
            const created = await postJson('/api/jobs-create.php', { url });
            lastJobId = created.id;
            lastJobResult = created;
            void persist();
            setJobState(created.message, created.progress);
            await pollJob(created.id);
        } catch (error) {
            setDetection('The request could not be completed.', false);
            setJobState(error.message, 100, true);
            showWorkspaceError(error, resultCard, resultContent, form, lastSubmittedUrl);
            void persist();
        } finally {
            setBusy(false);
        }
    };
    const paste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) {
                setDetection('Clipboard is empty.', false);
                return;
            }
            urlInput.value = text.trim();
            lastSubmittedUrl = urlInput.value;
            localStorage.setItem('saveflow:lastUrl', urlInput.value);
            setDetection('Clipboard URL pasted.', true);
            urlInput.focus();
            void persist();
            return true;
        } catch {
            setDetection('Clipboard access was blocked by the browser.', false);
            return false;
        }
    };
    const pasteAndAnalyze = async () => {
        const pasted = await paste();
        if (pasted && urlInput.value.trim()) {
            window.setTimeout(() => form.requestSubmit(), 50);
        }
    };
    const refreshPwaOnboarding = async () => {
        if (!pwaOnboardingStatus) {
            return;
        }

        if (isInstalled()) {
            pwaOnboardingStatus.textContent = 'SaveFlow is already running as an installed app.';
            if (installButton) installButton.hidden = true;
            return;
        }

        if (installButton) {
            installButton.hidden = false;
            installButton.disabled = false;
            installButton.textContent = window.SaveFlowPWA?.canInstall ? 'Install App' : 'How To Install';
        }

        if (window.SaveFlowPWA?.canInstall) {
            pwaOnboardingStatus.textContent = 'Install is available. Add SaveFlow to your device for faster reopening.';
            return;
        }

        try {
            const health = await getJson('/api/pwa-health.php', { redirectAuth: false });
            if (health.ok) {
                pwaOnboardingStatus.textContent = 'Install is supported from your browser menu on this device.';
                return;
            }
            pwaOnboardingStatus.textContent = health.blockers?.length ? `PWA setup issue: ${health.blockers.join(' ')}` : 'PWA setup issue detected.';
        } catch (error) {
            pwaOnboardingStatus.textContent = error.message;
        }
    };
    const installPwa = async () => {
        if (isInstalled()) {
            pwaOnboardingStatus.textContent = 'SaveFlow is already installed.';
            if (installButton) installButton.hidden = true;
            return;
        }

        if (!window.SaveFlowPWA?.canInstall) {
            pwaOnboardingStatus.textContent = 'Use your browser menu, then choose Install app or Add to Home screen.';
            window.SaveFlowA11y?.announce('Use your browser menu, then choose Install app or Add to Home screen.');
            return;
        }

        const result = await window.SaveFlowPWA.promptInstall();
        if (result?.outcome === 'accepted' || result?.outcome === 'installed') {
            pwaOnboardingStatus.textContent = 'SaveFlow install started.';
        } else {
            pwaOnboardingStatus.textContent = 'Install prompt is not available right now. Use the browser menu to install.';
        }
        if (installButton) {
            installButton.hidden = isInstalled();
            installButton.textContent = 'How To Install';
        }
    };
    const refreshNotificationUi = () => {
        if (!notificationStatus || !notificationButton) {
            return;
        }

        if (!('Notification' in window)) {
            notificationStatus.textContent = 'This browser does not support local job alerts.';
            notificationButton.hidden = true;
            return;
        }

        if (Notification.permission === 'granted') {
            notificationStatus.textContent = 'Local completion alerts are enabled for this browser.';
            notificationButton.hidden = true;
            return;
        }

        if (Notification.permission === 'denied') {
            notificationStatus.textContent = 'Local alerts are blocked in browser settings.';
            notificationButton.hidden = true;
            return;
        }

        notificationStatus.textContent = 'Enable optional local alerts for completed foreground jobs.';
        notificationButton.hidden = false;
    };
    const enableNotifications = async () => {
        if (!('Notification' in window)) {
            return;
        }
        const permission = await Notification.requestPermission();
        notificationStatus.textContent = permission === 'granted' ? 'Local completion alerts are enabled.' : 'Local alerts were not enabled.';
        refreshNotificationUi();
    };
    const notifyJobComplete = (job) => {
        if (!('Notification' in window) || Notification.permission !== 'granted' || lastNotifiedJobId === job.id) {
            return;
        }
        lastNotifiedJobId = job.id;
        const title = job.metadata?.title || job.result?.file_name || 'Download ready';
        try {
            new Notification('SaveFlow download ready', {
                body: title,
                tag: `saveflow-job-${job.id}`,
            });
        } catch {
            // Browser notification support is best-effort only.
        }
    };
    const renderResume = async () => {
        const resume = await loadWorkspaceResume();
        if (cancelled) {
            return;
        }
        const queued = Number(resume.queue?.counts?.queued || 0);
        const failed = Number(resume.queue?.counts?.failed || 0);
        const unfinished = resume.unfinished_jobs || [];
        const cards = [];

        if (resume.session?.draft_url) {
            cards.push(`<article class="history-card"><div><p class="eyebrow">Draft URL</p><h3>${escapeHtml(resume.session.draft_url)}</h3></div><div class="history-actions"><button type="button" data-restore-url="${escapeAttribute(resume.session.draft_url)}">Restore URL</button></div></article>`);
        }

        if (resume.session?.last_job_id) {
            cards.push(`<article class="history-card"><div><p class="eyebrow">Last job</p><h3>${escapeHtml(resume.session.last_job_id)}</h3><p class="hint">Resume the latest saved job status without creating a duplicate.</p></div><div class="history-actions"><button type="button" data-resume-job="${escapeAttribute(resume.session.last_job_id)}">Resume Job</button></div></article>`);
        }

        cards.push(...unfinished.map((job) => `<article class="history-card"><div><p class="eyebrow">${escapeHtml(job.platform)} - ${escapeHtml(job.status)}</p><h3>${escapeHtml(job.normalized_url)}</h3><p class="hint">${escapeHtml(job.message)} ${Number(job.progress || 0)}%</p></div><div class="history-actions"><a class="button-link secondary" href="/history.html">Open History</a></div></article>`));
        cards.push(...(resume.queue?.resumable_items || []).slice(0, 3).map((item) => `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(item.normalized_url)}</h3>${item.error ? `<p class="notice">${escapeHtml(item.error.message)}</p>` : ''}</div><div class="history-actions"><a class="button-link secondary" href="/queue.html">Open Queue</a></div></article>`));

        if (!cards.length) {
            resumeStatus.textContent = 'Nothing unfinished right now.';
            resumeContent.innerHTML = '<article class="history-card empty-state"><p>Queue items, failed jobs, and saved drafts will appear here when there is work to continue.</p></article>';
            return;
        }

        resumeStatus.textContent = `${queued} queued, ${failed} failed, ${unfinished.length} unfinished job${unfinished.length === 1 ? '' : 's'}, ${Number(resume.vault?.active_count || 0)} saved link${Number(resume.vault?.active_count || 0) === 1 ? '' : 's'}.`;
        resumeContent.innerHTML = cards.join('');
    };
    const processNext = async () => {
        processNextResume.disabled = true;
        resumeStatus.textContent = 'Processing next queue item...';
        try {
            const data = await postJson('/api/queue-process-next.php', {});
            lastJobId = data.processed?.job_id || null;
            lastJobResult = data.processed?.job_result || data.processed || null;
            resumeStatus.textContent = `Queue item ${data.processed.id} processed as ${data.processed.status}.`;
            window.SaveFlowA11y?.announce(resumeStatus.textContent);
            await renderResume();
        } catch (error) {
            resumeStatus.textContent = error.message;
        } finally {
            processNextResume.disabled = false;
        }
    };
    const clearSession = async () => {
        const confirmed = await window.SaveFlowA11y?.confirm({
            title: 'Clear workspace session',
            message: 'Clear saved draft, filters, and resume state for this browser workspace?',
            confirmLabel: 'Clear Session',
            returnFocusTo: clearResume,
        });
        if (confirmed === false) {
            return;
        }
        await clearWorkspaceSession();
        lastJobId = null;
        lastJobResult = null;
        resumeStatus.textContent = 'Workspace session cleared.';
        resumeContent.innerHTML = '<article class="history-card empty-state"><p>Saved workspace state has been cleared.</p></article>';
    };
    const restoreClick = (event) => {
        const restoreButton = event.target.closest('[data-restore-url]');
        if (restoreButton) {
            urlInput.value = restoreButton.dataset.restoreUrl || '';
            lastSubmittedUrl = urlInput.value;
            urlInput.focus();
            setDetection('Saved URL restored.', true);
            void persist();
            return;
        }

        const resumeButton = event.target.closest('[data-resume-job]');
        if (resumeButton) {
            void resumeLastJob(resumeButton.dataset.resumeJob || '');
        }
    };

    if (sharedUrl) {
        urlInput.value = sharedUrl;
        localStorage.setItem('saveflow:lastUrl', sharedUrl);
        setDetection('Shared URL loaded. Ready to analyze.', true);
        window.setTimeout(() => urlInput.focus(), 0);
        void persist();
    } else if (lastSubmittedUrl) {
        urlInput.value = lastSubmittedUrl;
        setDetection('Last URL restored. Ready when you are.', null);
    }

    void loadWorkspaceSession().then((saved) => {
        if (cancelled) {
            return;
        }
        const session = saved.session;
        const draftUrl = sharedUrl || session?.draft_url || lastSubmittedUrl;
        if (draftUrl) {
            urlInput.value = draftUrl;
            lastSubmittedUrl = draftUrl;
            setDetection(sharedUrl ? 'Shared URL loaded. Ready to analyze.' : 'Saved URL restored. Ready when you are.', sharedUrl ? true : null);
        }
        lastJobId = session?.last_job_id || null;
        lastJobResult = session?.last_result || null;
        if (!sharedUrl && lastJobId) {
            void resumeLastJob(lastJobId);
        }
        return renderResume();
    });
    void refreshPwaOnboarding();
    refreshNotificationUi();
    const displayModeCleanup = displayModeQuery?.addEventListener
        ? listen(displayModeQuery, 'change', refreshPwaOnboarding)
        : (() => {
            displayModeQuery?.addListener?.(refreshPwaOnboarding);
            return () => displayModeQuery?.removeListener?.(refreshPwaOnboarding);
        })();

    const cleanups = [
        listen(form, 'submit', submit),
        listen(pasteButton, 'click', paste),
        listen(mobilePasteButton, 'click', paste),
        listen(mobileAnalyzeButton, 'click', () => form.requestSubmit()),
        listen(mobilePasteAnalyzeButton, 'click', pasteAndAnalyze),
        listen(installButton, 'click', installPwa),
        listen(notificationButton, 'click', enableNotifications),
        listen(window, 'saveflow:pwa-install-ready', refreshPwaOnboarding),
        listen(window, 'saveflow:pwa-installed', refreshPwaOnboarding),
        displayModeCleanup,
        listen(urlInput, 'input', () => {
            lastSubmittedUrl = urlInput.value.trim();
            localStorage.setItem('saveflow:lastUrl', lastSubmittedUrl);
            void persist();
        }),
        listen(processNextResume, 'click', processNext),
        listen(clearResume, 'click', clearSession),
        listen(resumeContent, 'click', restoreClick),
    ];

    return () => {
        cancelled = true;
        cleanupAll(cleanups)();
    };
}

function initHistory() {
    const form = document.querySelector('#history-form');
    const statusText = document.querySelector('#history-status');
    const list = document.querySelector('#history-list');
    const exportCsv = document.querySelector('#export-history-csv');
    const exportResearchZip = document.querySelector('#export-history-research-zip');
    const exportMediaZip = document.querySelector('#export-history-media-zip');
    const prefKey = 'saveflow:historyFilters';

    if (!form || !list) {
        return () => {};
    }

    const savePreferences = () => {
        const filters = formFilters(form);
        localStorage.setItem(prefKey, JSON.stringify({
            platform: filters.platform || '',
            favorite: filters.favorite || '',
            sort: filters.sort || 'latest',
        }));
        void saveWorkspaceSession({ active_page: 'history', filters });
    };
    const restorePreferences = () => {
        try {
            const prefs = JSON.parse(localStorage.getItem(prefKey) || '{}');
            for (const [name, value] of Object.entries(prefs)) {
                const field = form.elements.namedItem(name);
                if (field && typeof value === 'string') {
                    field.value = value;
                }
            }
        } catch {
            localStorage.removeItem(prefKey);
        }
    };
    const load = async () => {
        statusText.textContent = 'Loading history...';
        list.innerHTML = '';
        try {
            const data = await getJson(`/api/history.php?${new URLSearchParams(new FormData(form)).toString()}`);
            if (!data.items.length) {
                statusText.textContent = 'No history items match this view yet.';
                list.innerHTML = '<article class="history-card empty-state"><p>Try clearing filters, or download a public URL from the workspace first.</p></article>';
                return;
            }
            statusText.textContent = `${data.items.length} item${data.items.length === 1 ? '' : 's'} shown.`;
            list.innerHTML = data.items.map(renderHistoryItem).join('');
        } catch (error) {
            statusText.textContent = error.message;
        }
    };
    const submit = (event) => {
        event.preventDefault();
        savePreferences();
        void load();
    };
    const click = async (event) => {
        const actionSheet = event.target.closest('[data-sheet-actions]');
        if (actionSheet) {
            openActionSheetFromDataset(actionSheet);
            return;
        }
        const favorite = event.target.closest('[data-favorite-id]');
        if (favorite) {
            await postJson('/api/favorites-toggle.php', { id: favorite.dataset.favoriteId, favorite: favorite.dataset.favorite !== 'true' });
            await load();
            return;
        }
        const retry = event.target.closest('[data-retry-id]');
        if (retry) {
            const confirmed = await window.SaveFlowA11y?.confirm({ title: 'Retry job', message: 'Start a new job from this saved URL?', confirmLabel: 'Retry', returnFocusTo: retry });
            if (confirmed === false) return;
            const job = await postJson('/api/jobs-retry.php', { id: retry.dataset.retryId });
            statusText.textContent = `Retry job ${job.id} started.`;
            return;
        }
        const note = event.target.closest('[data-note-id]');
        if (note) {
            const textarea = document.querySelector(`[data-note-for="${cssEscape(note.dataset.noteId)}"]`);
            await postJson('/api/history-note.php', { id: note.dataset.noteId, note: textarea?.value || '' });
            note.textContent = 'Saved';
            window.setTimeout(() => { note.textContent = 'Save Note'; }, 900);
            return;
        }
        const collection = event.target.closest('[data-collection-id]');
        if (collection) {
            await chooseCollectionForJob(collection.dataset.collectionId, collection, statusText);
        }
    };
    const exportHistory = async (button, packageType = null) => {
        const endpoint = packageType ? '/api/archive-export-history-zip.php' : '/api/archive-export-history-csv.php';
        const confirmed = await window.SaveFlowA11y?.confirm({
            title: packageType ? 'Export history ZIP' : 'Export history CSV',
            message: packageType ? 'Generate an archive ZIP from the current history filters?' : 'Generate a CSV file from the current history filters?',
            confirmLabel: packageType ? 'Export ZIP' : 'Export CSV',
            returnFocusTo: button,
        });
        if (confirmed === false) return;
        button.disabled = true;
        statusText.textContent = packageType ? 'Creating archive ZIP...' : 'Creating history CSV...';
        try {
            const body = { filters: formFilters(form) };
            if (packageType) body.package_type = packageType;
            const data = await postJson(endpoint, body);
            statusText.innerHTML = `${escapeHtml(data.file_name)} is ready. <a href="${escapeAttribute(data.download_url)}">Download ${packageType ? 'ZIP' : 'CSV'}</a>`;
        } catch (error) {
            statusText.textContent = error.message;
        } finally {
            button.disabled = false;
        }
    };

    restorePreferences();
    void load();
    const filterCleanup = installSavedFilters({ scope: 'history', form, statusText, onApply: () => { savePreferences(); void load(); } });
    return cleanupAll([
        filterCleanup,
        listen(form, 'submit', submit),
        listen(form, 'change', savePreferences),
        listen(list, 'click', click),
        listen(exportCsv, 'click', () => exportHistory(exportCsv)),
        listen(exportResearchZip, 'click', () => exportHistory(exportResearchZip, 'research_metadata_zip')),
        listen(exportMediaZip, 'click', () => exportHistory(exportMediaZip, 'media_archive_zip')),
    ]);
}

function initQueue() {
    return initQueueLike({
        page: 'queue',
        formSelector: '#queue-form',
        filterSelector: '#queue-filter-form',
        listSelector: '#queue-list',
        statusSelector: '#queue-status',
        addEndpoint: '/api/queue-add.php',
        listEndpoint: '/api/queue.php',
        addPayload: (form) => ({ urls: new FormData(form).get('urls') || '', note: new FormData(form).get('note') || '' }),
        renderItem: renderQueueItem,
        emptyText: 'Paste links above to create your first queue items.',
        savedFilterScope: 'queue',
    });
}

function initVault() {
    return initQueueLike({
        page: 'vault',
        formSelector: '#vault-form',
        filterSelector: '#vault-filter-form',
        listSelector: '#vault-list',
        statusSelector: '#vault-status',
        addEndpoint: '/api/link-vault-save.php',
        listEndpoint: '/api/link-vault.php',
        addPayload: (form) => ({ url: new FormData(form).get('url') || '', note: new FormData(form).get('note') || '' }),
        renderItem: renderVaultItem,
        emptyText: 'Save useful links above, then move them into the queue when ready.',
        savedFilterScope: 'vault',
    });
}

function initQueueLike(config) {
    const form = document.querySelector(config.formSelector);
    const filterForm = document.querySelector(config.filterSelector);
    const list = document.querySelector(config.listSelector);
    const statusText = document.querySelector(config.statusSelector);
    const refreshButton = document.querySelector(config.page === 'queue' ? '#refresh-queue' : '#refresh-vault');
    const processNextButton = document.querySelector('#process-next-queue');

    if (!form || !filterForm || !list) {
        return () => {};
    }

    const persist = (extra = {}) => saveWorkspaceSession({
        active_page: config.page,
        filters: formFilters(filterForm),
        draft_text: form.elements.namedItem('urls')?.value || '',
        [`${config.page}_context`]: { note: form.elements.namedItem('note')?.value || '' },
        ...extra,
    });
    const load = async () => {
        statusText.textContent = `Loading ${config.page}...`;
        list.innerHTML = '';
        try {
            const data = await getJson(`${config.listEndpoint}?${new URLSearchParams(new FormData(filterForm)).toString()}`);
            if (!data.items.length) {
                statusText.textContent = `${config.page === 'queue' ? 'Queue' : 'Vault'} is empty.`;
                list.innerHTML = `<article class="history-card empty-state"><p>${config.emptyText}</p></article>`;
                return;
            }
            statusText.textContent = `${data.items.length} item${data.items.length === 1 ? '' : 's'} shown.`;
            list.innerHTML = data.items.map(config.renderItem).join('');
            void persist();
        } catch (error) {
            statusText.textContent = error.message;
        }
    };
    const submit = async (event) => {
        event.preventDefault();
        try {
            const result = await postJson(config.addEndpoint, config.addPayload(form));
            statusText.textContent = result.items ? `${result.items.length} item${result.items.length === 1 ? '' : 's'} added.` : 'Saved.';
            form.reset();
            void persist();
            await load();
        } catch (error) {
            statusText.textContent = error.message;
        }
    };
    const click = async (event) => {
        const sheet = event.target.closest('[data-sheet-actions]');
        if (sheet) {
            openActionSheetFromDataset(sheet);
            return;
        }
        const process = event.target.closest('[data-process-id]');
        if (process) {
            process.disabled = true;
            try {
                await postJson('/api/queue-process.php', { id: Number(process.dataset.processId) });
                await load();
            } catch (error) {
                statusText.textContent = error.message;
                process.disabled = false;
            }
            return;
        }
        const move = event.target.closest('[data-move-id]');
        if (move) {
            await postJson('/api/link-vault-to-queue.php', { id: Number(move.dataset.moveId) });
            statusText.textContent = 'Moved to queue.';
            await load();
            return;
        }
        const archive = event.target.closest('[data-archive-id]');
        if (archive) {
            const isArchived = archive.dataset.archive === 'true';
            const endpoint = config.page === 'queue' ? '/api/queue-archive.php' : '/api/link-vault-archive.php';
            await postJson(endpoint, { id: Number(archive.dataset.archiveId), archive: !isArchived });
            await load();
        }
    };
    const processNext = async () => {
        processNextButton.disabled = true;
        try {
            const filters = formFilters(filterForm);
            const result = await postJson('/api/queue-process-next.php', { platform: filters.platform || '' });
            statusText.textContent = `Queue item ${result.processed.id} processed as ${result.processed.status}.`;
            await load();
        } catch (error) {
            statusText.textContent = error.message;
        } finally {
            processNextButton.disabled = false;
        }
    };
    const filterCleanup = installSavedFilters({ scope: config.savedFilterScope, form: filterForm, statusText, onApply: () => { void persist(); void load(); } });

    void load();
    return cleanupAll([
        filterCleanup,
        listen(form, 'submit', submit),
        listen(refreshButton, 'click', load),
        listen(processNextButton, 'click', processNext),
        listen(filterForm, 'submit', (event) => { event.preventDefault(); void persist(); void load(); }),
        listen(filterForm, 'change', () => void persist()),
        listen(list, 'click', click),
    ]);
}

function initCollections() {
    const form = document.querySelector('#collection-form');
    const filterForm = document.querySelector('#collection-filter-form');
    const list = document.querySelector('#collection-list');
    const status = document.querySelector('#collection-status');
    const detail = document.querySelector('#collection-detail');
    const detailStatus = document.querySelector('#collection-detail-status');
    const exportsList = document.querySelector('#archive-export-list');
    const exportsStatus = document.querySelector('#archive-export-status');
    const refreshExports = document.querySelector('#refresh-exports');
    let activeCollection = null;

    if (!form || !filterForm || !list) return () => {};

    const loadCollections = async () => {
        status.textContent = 'Loading collections...';
        const data = await getJson(`/api/collections.php?${new URLSearchParams(new FormData(filterForm)).toString()}`);
        status.textContent = `${data.items.length} collection${data.items.length === 1 ? '' : 's'} shown.`;
        list.innerHTML = data.items.length ? data.items.map(renderCollection).join('') : '<article class="history-card empty-state"><p>Create a collection above, then add jobs from History or Inspector.</p></article>';
    };
    const loadDetail = async (id) => {
        activeCollection = id;
        const data = await getJson(`/api/collection-detail.php?id=${encodeURIComponent(id)}`);
        detailStatus.textContent = `${data.items.length} item${data.items.length === 1 ? '' : 's'} in ${data.collection.name}.`;
        detail.innerHTML = data.items.length ? data.items.map(renderCollectionItem).join('') : '<article class="history-card empty-state"><p>This collection is empty. Add jobs from History or Inspector.</p></article>';
    };
    const loadExports = async () => {
        const data = await getJson('/api/archive-exports.php');
        exportsStatus.textContent = `${data.items.length} export${data.items.length === 1 ? '' : 's'} available.`;
        exportsList.innerHTML = data.items.length ? data.items.map(renderArchiveExport).join('') : '<article class="history-card empty-state"><p>Generate a CSV or ZIP to see it here.</p></article>';
    };
    const submit = async (event) => {
        event.preventDefault();
        const data = Object.fromEntries(new FormData(form).entries());
        await postJson('/api/collections-save.php', data);
        form.reset();
        await loadCollections();
    };
    const clickList = async (event) => {
        const open = event.target.closest('[data-open-id]');
        const edit = event.target.closest('[data-edit-id]');
        const archive = event.target.closest('[data-archive-id]');
        if (open) await loadDetail(open.dataset.openId);
        if (edit) {
            form.elements.namedItem('id').value = edit.dataset.editId;
            form.elements.namedItem('name').value = edit.dataset.name || '';
            form.elements.namedItem('description').value = edit.dataset.description || '';
            form.elements.namedItem('color_label').value = edit.dataset.color || '';
        }
        if (archive) {
            await postJson('/api/collections-archive.php', { id: Number(archive.dataset.archiveId), archive: archive.dataset.archived !== 'true' });
            await loadCollections();
        }
    };
    const clickDetail = async (event) => {
        const remove = event.target.closest('[data-remove-job]');
        const tag = event.target.closest('[data-filter-tag]');
        if (remove) {
            await postJson('/api/collections-remove-job.php', { collection_item_id: Number(remove.dataset.removeJob) });
            await loadDetail(activeCollection);
        }
        if (tag) {
            filterForm.elements.namedItem('tag').value = tag.dataset.filterTag;
            await loadCollections();
        }
    };
    const exportCollection = async (button, type = null) => {
        if (!activeCollection) {
            detailStatus.textContent = 'Choose a collection first.';
            return;
        }
        const endpoint = type ? '/api/archive-export-collection-zip.php' : '/api/archive-export-collection-csv.php';
        const body = { collection_id: Number(activeCollection) };
        if (type) body.package_type = type;
        const data = await postJson(endpoint, body);
        detailStatus.innerHTML = `${escapeHtml(data.file_name)} is ready. <a href="${escapeAttribute(data.download_url)}">Download</a>`;
        await loadExports();
    };
    const filterCleanup = installSavedFilters({ scope: 'collections', form: filterForm, statusText: status, onApply: loadCollections });

    void loadCollections();
    void loadExports();
    return cleanupAll([
        filterCleanup,
        listen(form, 'submit', submit),
        listen(filterForm, 'submit', (event) => { event.preventDefault(); void loadCollections(); }),
        listen(filterForm, 'change', () => void saveWorkspaceSession({ active_page: 'collections', filters: formFilters(filterForm) })),
        listen(list, 'click', clickList),
        listen(detail, 'click', clickDetail),
        listen(refreshExports, 'click', loadExports),
        listen(document.querySelector('#refresh-detail'), 'click', () => activeCollection && loadDetail(activeCollection)),
        listen(document.querySelector('#export-collection-csv'), 'click', (event) => exportCollection(event.currentTarget)),
        listen(document.querySelector('#export-collection-research-zip'), 'click', (event) => exportCollection(event.currentTarget, 'research_metadata_zip')),
        listen(document.querySelector('#export-collection-media-zip'), 'click', (event) => exportCollection(event.currentTarget, 'media_archive_zip')),
    ]);
}

function initResearch() {
    const form = document.querySelector('#research-form');
    const status = document.querySelector('#research-status');
    const list = document.querySelector('#research-list');
    const collectionFilter = document.querySelector('#research-collection-filter');
    const targetCollection = document.querySelector('#research-target-collection');
    if (!form || !list) return () => {};

    const load = async () => {
        status.textContent = 'Loading research workspace...';
        const data = await getJson(`/api/research.php?${new URLSearchParams(new FormData(form)).toString()}`);
        renderCollectionOptions(data.collections || [], collectionFilter, targetCollection);
        status.textContent = `${data.items.length} research item${data.items.length === 1 ? '' : 's'} shown.`;
        list.innerHTML = data.items.length ? data.items.map(renderResearchItem).join('') : '<article class="history-card empty-state"><p>Try clearing filters, adding jobs to collections, or downloading a public URL first.</p></article>';
    };
    const click = async (event) => {
        const tag = event.target.closest('[data-filter-tag]');
        if (tag) {
            form.elements.namedItem('tag').value = tag.dataset.filterTag;
            await load();
            return;
        }
        const note = event.target.closest('[data-save-note]');
        if (note) {
            const field = document.querySelector(`[data-note-for="${cssEscape(note.dataset.saveNote)}"]`);
            await postJson('/api/research-note.php', { id: note.dataset.saveNote, note: field?.value || '' });
            status.textContent = 'Note saved.';
        }
    };
    const exportResearch = async (kind, button) => {
        const endpoint = kind === 'csv' ? '/api/archive-export-history-csv.php' : '/api/archive-export-history-zip.php';
        const body = { filters: formFilters(form) };
        if (kind !== 'csv') body.package_type = kind;
        const data = await postJson(endpoint, body);
        status.innerHTML = `${escapeHtml(data.file_name)} is ready. <a href="${escapeAttribute(data.download_url)}">Download</a>`;
    };

    void load();
    return cleanupAll([
        listen(form, 'submit', (event) => { event.preventDefault(); void load(); }),
        listen(form, 'change', () => void saveWorkspaceSession({ active_page: 'research', filters: formFilters(form), selected_collection_id: targetCollection.value || null })),
        listen(targetCollection, 'change', () => void saveWorkspaceSession({ active_page: 'research', filters: formFilters(form), selected_collection_id: targetCollection.value || null })),
        listen(list, 'click', click),
        listen(document.querySelector('#select-visible'), 'click', () => document.querySelectorAll('[data-select-job]').forEach((field) => { field.checked = true; })),
        listen(document.querySelector('#clear-selection'), 'click', () => document.querySelectorAll('[data-select-job]').forEach((field) => { field.checked = false; })),
        listen(document.querySelector('#add-selected-collection'), 'click', async () => {
            const collectionId = Number(targetCollection.value || 0);
            const selected = [...document.querySelectorAll('[data-select-job]:checked')].map((field) => field.value);
            for (const id of selected) await postJson('/api/collections-add-job.php', { collection_id: collectionId, job_id: id });
            status.textContent = `${selected.length} item${selected.length === 1 ? '' : 's'} added.`;
        }),
        listen(document.querySelector('#export-research-csv'), 'click', (event) => exportResearch('csv', event.currentTarget)),
        listen(document.querySelector('#export-research-zip'), 'click', (event) => exportResearch('research_metadata_zip', event.currentTarget)),
        listen(document.querySelector('#export-media-zip'), 'click', (event) => exportResearch('media_archive_zip', event.currentTarget)),
    ]);
}

function initSettings() {
    const fields = {
        database: document.querySelector('#database-status'),
        ytdlp: document.querySelector('#ytdlp-status'),
        retention: document.querySelector('#retention-status'),
        cleanup: document.querySelector('#cleanup-status'),
        history: document.querySelector('#history-status'),
        pwa: document.querySelector('#pwa-status'),
        platform: document.querySelector('#platform-status'),
        cookieless: document.querySelector('#cookieless-status'),
        youtube: document.querySelector('#youtube-status'),
        tiktok: document.querySelector('#tiktok-status'),
        instagram: document.querySelector('#instagram-status'),
        runtime: document.querySelector('#runtime-status'),
        account: document.querySelector('#account-status'),
        mail: document.querySelector('#mail-status'),
        detail: document.querySelector('#pwa-detail'),
    };
    const refresh = document.querySelector('#refresh-health');
    const formatDateTime = (value) => {
        if (!value) return '';
        const parsed = new Date(String(value).replace(' ', 'T'));
        return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
    };
    const formatCleanup = (cleanup) => {
        const lastRun = cleanup?.last_run;
        if (!lastRun) return 'No cleanup run recorded yet. Cleanup stays CLI-only for safety.';
        const finished = lastRun.finished_at ? `Finished ${formatDateTime(lastRun.finished_at)}` : `Started ${formatDateTime(lastRun.started_at)}`;
        const deleted = `${Number(lastRun.deleted_files || 0)} file${Number(lastRun.deleted_files || 0) === 1 ? '' : 's'}, ${Number(lastRun.deleted_jobs || 0)} job${Number(lastRun.deleted_jobs || 0) === 1 ? '' : 's'}`;
        return `${finished}. ${deleted}. ${lastRun.message || 'Cleanup status recorded.'}`;
    };
    const formatYtDlp = (ytDlp) => {
        if (!ytDlp) return 'Could not complete the health check.';
        if (!ytDlp.ok) {
            const warning = Array.isArray(ytDlp.warnings) && ytDlp.warnings.length ? ytDlp.warnings.join(' ') : '';
            return warning || 'Download engine is not ready. Check SAVEFLOW_YTDLP_BINARY.';
        }
        const version = ytDlp.version || 'available';
        const age = Number.isFinite(Number(ytDlp.version_age_days)) ? `, ${ytDlp.version_age_days} days old` : '';
        const binary = ytDlp.binary ? ` Binary: ${ytDlp.binary}.` : '';
        const warnings = Array.isArray(ytDlp.warnings) && ytDlp.warnings.length ? ` ${ytDlp.warnings.join(' ')}` : '';
        return `Ready: yt-dlp ${version}${age}.${binary}${warnings}`;
    };
    const formatPlatforms = (ytDlp) => {
        const platforms = ytDlp?.platforms || {};
        const tiktok = platforms.tiktok || {};
        const youtube = platforms.youtube || {};
        const instagram = platforms.instagram || {};
        const cookies = ytDlp?.cookies || {};
        const cookiePlatforms = Array.isArray(cookies.platforms) && cookies.platforms.length ? cookies.platforms.join(', ') : 'none';
        const cookieState = cookies.enabled
            ? (cookies.readable ? `admin cookies enabled for ${cookiePlatforms}` : 'admin cookies enabled, but the file is not readable')
            : 'admin cookies disabled for account safety';
        const parts = [
            `YouTube ${youtube.po_token_configured || youtube.po_provider_configured ? 'PO token support configured' : 'using cookieless clients'}`,
            `TikTok ${tiktok.app_info_configured ? 'app info configured' : 'using default extractor settings'}`,
            `Instagram ${instagram.extractor_args_configured ? 'extractor args configured' : 'using default extractor settings'}`,
            cookieState,
        ];
        if (instagram.user_agent_configured || ytDlp?.user_agent_configured) parts.push('custom user agent configured');
        if (ytDlp?.impersonate_configured) parts.push('browser impersonation configured');
        return `${parts.join('. ')}.`;
    };
    const formatCookieless = (ytDlp) => {
        if (!ytDlp) return 'Cookieless readiness could not be checked.';
        if (ytDlp.cookieless_mode) return 'Cookieless mode active. SaveFlow will not send account cookies to yt-dlp.';
        return 'Account cookies are enabled. Use only for owner-approved URLs and understand account risk.';
    };
    const formatYouTube = (ytDlp) => {
        const youtube = ytDlp?.platforms?.youtube || {};
        const clients = Array.isArray(youtube.clients) && youtube.clients.length ? youtube.clients.join(', ') : 'yt-dlp defaults';
        const po = youtube.po_token_configured ? 'PO token configured' : (youtube.po_provider_configured ? 'PO provider configured' : 'PO token not configured');
        const visitor = youtube.visitor_data_configured ? 'visitor data configured' : 'visitor data not configured';
        return `Clients: ${clients}. ${po}. ${visitor}.`;
    };
    const formatTikTok = (ytDlp) => {
        const tiktok = ytDlp?.platforms?.tiktok || {};
        const parts = [
            tiktok.app_info_configured ? 'app_info configured' : 'app_info not configured',
            tiktok.device_id_configured ? 'device_id configured' : 'random/default device id',
            tiktok.api_hostname_configured ? 'API hostname configured' : 'default API hostname',
        ];
        return parts.join('. ') + '.';
    };
    const formatInstagram = (ytDlp) => {
        const instagram = ytDlp?.platforms?.instagram || {};
        const parts = [
            instagram.app_id_configured ? 'app ID configured' : 'app ID not configured',
            instagram.extractor_args_configured ? 'extra extractor args configured' : 'default extractor args',
        ];
        return parts.join('. ') + '.';
    };
    const formatRuntime = (ytDlp) => {
        const dependencies = ytDlp?.dependencies || {};
        const js = dependencies.javascript_runtime || {};
        const ejs = dependencies.ejs || {};
        const impersonation = dependencies.impersonation || {};
        const parts = [
            js.available ? `JS runtime: ${js.command}${js.version ? ` ${js.version}` : ''}` : 'No JS runtime detected',
            ejs.available ? 'yt-dlp-ejs available' : 'yt-dlp-ejs not detected',
            impersonation.supported ? 'yt-dlp supports --impersonate' : 'impersonation support not confirmed',
        ];
        return parts.join('. ') + '.';
    };
    const formatMail = (mail) => {
        if (!mail) return 'Mail readiness could not be checked.';
        const driver = mail.driver ? `${mail.driver.toUpperCase()} mail` : 'Mail';
        if (mail.ok) return `${driver} is configured.`;
        return mail.warning || `${driver} is not fully configured. Recovery codes remain available.`;
    };
    const load = async () => {
        try {
            const [healthResponse, pwaResponse, authResponse] = await Promise.all([
                fetch('/api/health.php', { headers: { Accept: 'application/json' } }),
                fetch('/api/pwa-health.php', { headers: { Accept: 'application/json' } }),
                fetch('/api/auth-state.php', { headers: { Accept: 'application/json' } }),
            ]);
            const health = await healthResponse.json();
            const pwa = await pwaResponse.json();
            const auth = await authResponse.json();
            fields.database.textContent = health.ok && health.data?.database?.ok ? (health.data.database.message || 'Database connection is ready.') : (health.data?.database?.message || 'SaveFlow could not start the required command.');
            fields.ytdlp.textContent = formatYtDlp(health.data?.yt_dlp);
            fields.retention.textContent = Number.isFinite(Number(health.data?.retention_hours)) ? `${health.data.retention_hours} hours retention.` : 'Could not read retention settings.';
            fields.cleanup.textContent = formatCleanup(health.data?.cleanup);
            fields.history.textContent = health.data?.history ? `${health.data.history.total || 0} history items, ${health.data.history.completed || 0} completed.` : 'Could not read history stats.';
            fields.pwa.textContent = pwa.data?.ok ? 'Install assets ready.' : 'PWA blockers detected.';
            fields.detail.textContent = pwa.data?.blockers?.length ? pwa.data.blockers.join(' ') : 'Manifest, service worker, offline page, app icons, app shortcuts, share target, HTTPS, and app shell assets are ready.';
            fields.platform.textContent = formatPlatforms(health.data?.yt_dlp);
            fields.cookieless.textContent = formatCookieless(health.data?.yt_dlp);
            fields.youtube.textContent = formatYouTube(health.data?.yt_dlp);
            fields.tiktok.textContent = formatTikTok(health.data?.yt_dlp);
            fields.instagram.textContent = formatInstagram(health.data?.yt_dlp);
            fields.runtime.textContent = formatRuntime(health.data?.yt_dlp);
            fields.mail.textContent = formatMail(health.data?.mail);
            fields.account.textContent = auth.data?.authenticated ? `Logged in as ${auth.data.user.display_name}.` : 'Not logged in.';
        } catch (error) {
            Object.values(fields).forEach((field) => {
                if (field) field.textContent = error.message;
            });
        }
    };
    void load();
    return listen(refresh, 'click', load);
}

function initAccount() {
    const status = document.querySelector('#account-status');
    const summary = document.querySelector('#account-summary');
    const refresh = document.querySelector('#refresh-account');
    const panels = ['register', 'login', 'profile', 'password', 'logout', 'recovery', 'sessions'].reduce((acc, key) => ({ ...acc, [key]: document.querySelector(`#${key}-panel`) }), {});
    const forms = {
        register: document.querySelector('#register-form'),
        login: document.querySelector('#login-form'),
        profile: document.querySelector('#profile-form'),
        password: document.querySelector('#password-form'),
    };
    const sessionsList = document.querySelector('#sessions-list');
    const sessionsStatus = document.querySelector('#sessions-status');
    const recoveryStatus = document.querySelector('#recovery-status');
    const recoveryCodes = document.querySelector('#recovery-codes');
    let csrf = '';

    if (!status || !refresh) return () => {};

    const renderState = (state) => {
        const user = state.user || null;
        csrf = state.csrf_token || '';
        panels.register.hidden = state.authenticated || !state.registration_available;
        panels.login.hidden = state.authenticated || state.registration_available;
        panels.profile.hidden = !state.authenticated;
        panels.password.hidden = !state.authenticated;
        panels.logout.hidden = !state.authenticated;
        panels.recovery.hidden = !state.authenticated;
        panels.sessions.hidden = !state.authenticated;
        if (!state.authenticated) {
            status.textContent = state.registration_available ? 'No owner account exists yet.' : 'Owner account exists. Log in to manage it.';
            summary.textContent = state.registration_available ? 'Create the owner account to attach existing local rows.' : 'Log in to view local ownership counts.';
            return;
        }
        status.textContent = `Logged in as ${user.display_name} (${user.username}).`;
        forms.profile.elements.namedItem('email').value = user.email || '';
        forms.profile.elements.namedItem('username').value = user.username || '';
        forms.profile.elements.namedItem('display_name').value = user.display_name || '';
    };
    const loadSummary = async () => {
        const data = await getJson('/api/account-summary.php');
        summary.textContent = `${data.jobs} jobs, ${data.queue_items} queue items, ${data.vault_items} vault links, ${data.collections} collections.`;
    };
    const loadSessions = async () => {
        const data = await getJson('/api/auth-sessions.php');
        sessionsStatus.textContent = `${data.sessions.length} active session${data.sessions.length === 1 ? '' : 's'}.`;
        sessionsList.innerHTML = data.sessions.map((session) => `<article class="history-card"><div><p class="eyebrow">${session.id === data.current_session_id ? 'Current session' : 'Session'}</p><h3>${escapeHtml(session.device_label || session.user_agent || 'Browser')}</h3><p class="hint">Last seen: ${escapeHtml(session.last_seen_at || session.created_at)}</p></div><div class="history-actions">${session.id === data.current_session_id ? '' : `<button type="button" data-revoke-session="${session.id}">Revoke</button>`}</div></article>`).join('');
    };
    const load = async () => {
        try {
            status.textContent = 'Checking account...';
            const state = await getJson('/api/auth-state.php');
            renderState(state);
            if (state.authenticated) {
                await loadSummary();
                await loadSessions();
            }
        } catch (error) {
            status.textContent = error.message;
        }
    };
    const submitForm = async (event, endpoint, success) => {
        event.preventDefault();
        const form = event.currentTarget;
        const button = form.querySelector('button[type="submit"]');
        button.disabled = true;
        status.textContent = 'Working...';
        try {
            await postJson(endpoint, { ...Object.fromEntries(new FormData(form).entries()), csrf_token: csrf });
            status.textContent = success;
            await load();
            if (endpoint === '/api/auth-login.php') {
                const next = new URLSearchParams(window.location.search).get('next');
                if (next && next.startsWith('/')) {
                    window.history.pushState({}, '', next);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }
            }
        } catch (error) {
            status.textContent = error.message;
            window.SaveFlowA11y?.announce(error.message);
        } finally {
            button.disabled = false;
        }
    };
    const logout = async () => {
        await postJson('/api/auth-logout.php', { csrf_token: csrf });
        status.textContent = 'Logged out.';
        await load();
    };
    const generateCodes = async () => {
        const data = await postJson('/api/auth-recovery-codes-generate.php', { csrf_token: csrf });
        recoveryStatus.textContent = 'Recovery codes generated. Save them now.';
        recoveryCodes.hidden = false;
        recoveryCodes.innerHTML = `<pre>${escapeHtml(data.codes.join('\n'))}</pre>`;
    };
    const sessionClick = async (event) => {
        const revoke = event.target.closest('[data-revoke-session]');
        if (!revoke) return;
        await postJson('/api/auth-session-revoke.php', { id: Number(revoke.dataset.revokeSession), csrf_token: csrf });
        await loadSessions();
    };

    void load();
    return cleanupAll([
        listen(refresh, 'click', load),
        listen(forms.register, 'submit', (event) => submitForm(event, '/api/auth-register.php', 'Account created.')),
        listen(forms.login, 'submit', (event) => submitForm(event, '/api/auth-login.php', 'Logged in.')),
        listen(forms.profile, 'submit', (event) => submitForm(event, '/api/auth-update-profile.php', 'Profile saved.')),
        listen(forms.password, 'submit', (event) => submitForm(event, '/api/auth-change-password.php', 'Password changed. Log in again.')),
        listen(document.querySelector('#logout-button'), 'click', logout),
        listen(document.querySelector('#generate-recovery-codes'), 'click', generateCodes),
        listen(document.querySelector('#refresh-sessions'), 'click', loadSessions),
        listen(document.querySelector('#revoke-other-sessions'), 'click', async () => { await postJson('/api/auth-session-revoke-others.php', { csrf_token: csrf }); await loadSessions(); }),
        listen(sessionsList, 'click', sessionClick),
    ]);
}

function initResetPassword() {
    const status = document.querySelector('#reset-status');
    const requestForm = document.querySelector('#request-reset-form');
    const completeForm = document.querySelector('#complete-reset-form');
    const recoveryForm = document.querySelector('#recovery-code-form');
    const submit = async (event, endpoint, message) => {
        event.preventDefault();
        const button = event.currentTarget.querySelector('button[type="submit"]');
        button.disabled = true;
        try {
            await postJson(endpoint, Object.fromEntries(new FormData(event.currentTarget).entries()));
            status.textContent = message;
        } catch (error) {
            status.textContent = error.message;
        } finally {
            button.disabled = false;
        }
    };
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
        completeForm.elements.namedItem('token').value = token;
    }
    return cleanupAll([
        listen(requestForm, 'submit', (event) => submit(event, '/api/auth-password-reset-request.php', 'If that email exists, reset instructions are ready.')),
        listen(completeForm, 'submit', (event) => submit(event, '/api/auth-password-reset-complete.php', 'Password changed. You are logged in.')),
        listen(recoveryForm, 'submit', (event) => submit(event, '/api/auth-recovery-code-login.php', 'Recovery code accepted. You are logged in.')),
    ]);
}

function initInspector() {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get('id') || '';
    const summary = document.querySelector('#inspector-summary');
    const thumb = document.querySelector('#inspector-thumb');
    const details = document.querySelector('#media-details');
    const downloadList = document.querySelector('#download-list');
    const exportStatus = document.querySelector('#export-status');
    const exportResult = document.querySelector('#export-result');
    const readingContent = document.querySelector('#reading-content');
    const prefStatus = document.querySelector('#workspace-pref-status');
    const addCollection = document.querySelector('#add-inspector-collection');
    const exportEndpoints = {
        'metadata-json': '/api/export-metadata-json.php',
        'metadata-txt': '/api/export-metadata-txt.php',
        'caption-txt': '/api/export-caption-txt.php',
        'bundle-zip': '/api/export-bundle-zip.php',
    };
    const load = async () => {
        if (!jobId) throw new Error('Open a completed job from History to inspect it.');
        const item = await getJson(`/api/media-inspect.php?id=${encodeURIComponent(jobId)}`);
        summary.textContent = item.title || item.file_name || 'Media details loaded.';
        thumb.innerHTML = item.thumbnail_url ? `<img src="${escapeAttribute(item.thumbnail_url)}" alt="">` : '<div class="thumb-placeholder large" aria-hidden="true"></div>';
        details.innerHTML = `<li><span>Status:</span> ${escapeHtml(item.status)}</li><li><span>Platform:</span> ${escapeHtml(item.platform)}</li><li><span>Author:</span> ${escapeHtml(item.author || 'Unknown')}</li><li><span>Size:</span> ${formatBytes(item.size_bytes)}</li><li><span>Type:</span> ${escapeHtml(item.mime_type || 'Unknown')}</li>`;
        downloadList.innerHTML = item.downloads?.length ? item.downloads.map(renderDownload).join('') : '<article class="history-card empty-state"><p>No registered downloads are available.</p></article>';
        readingContent.innerHTML = `<dl class="reading-list"><dt>Title</dt><dd>${escapeHtml(item.title || 'Untitled')}</dd><dt>Author</dt><dd>${escapeHtml(item.author || 'Unknown')}</dd><dt>Source URL</dt><dd>${escapeHtml(item.source_url || item.normalized_url || '')}</dd></dl>`;
    };
    const click = async (event) => {
        const sheet = event.target.closest('[data-sheet-actions]');
        if (sheet) return openActionSheetFromDataset(sheet);
        const exportButton = event.target.closest('[data-export]');
        const packageButton = event.target.closest('[data-package]');
        if (!exportButton && !packageButton) return;
        const endpoint = exportButton ? exportEndpoints[exportButton.dataset.export] : '/api/export-package.php';
        const body = exportButton ? { id: jobId } : { id: jobId, package_type: packageButton.dataset.package };
        const data = await postJson(endpoint, body);
        exportStatus.textContent = `${data.file_name || data.source_label || 'Export'} is ready.`;
        exportResult.innerHTML = renderDownload(data);
    };
    void getJson('/api/workspace-preferences.php').then(() => { prefStatus.textContent = 'Workspace defaults loaded.'; }).catch(() => { prefStatus.textContent = 'Workspace defaults unavailable.'; });
    void load().catch((error) => { summary.textContent = error.message; });
    return cleanupAll([
        listen(document, 'click', click),
        listen(addCollection, 'click', () => chooseCollectionForJob(jobId, addCollection, exportStatus)),
    ]);
}

function initOrganize() {
    const form = document.querySelector('#rule-form');
    const list = document.querySelector('#rules-list');
    const status = document.querySelector('#rules-status');
    const recipeForm = document.querySelector('#recipe-form');
    const recipeList = document.querySelector('#recipes-list');
    const recipeStatus = document.querySelector('#recipes-status');
    const collectionSelect = document.querySelector('#rule-collection');
    const preferencesForm = document.querySelector('#preferences-form');
    if (!form) return () => {};

    const loadRules = async () => {
        const data = await getJson('/api/auto-rules.php');
        status.textContent = `${data.items.length} rule${data.items.length === 1 ? '' : 's'} shown.`;
        list.innerHTML = data.items.length ? data.items.map(renderRule).join('') : '<article class="history-card empty-state"><p>Create a rule above to automate favorite, archive, or note actions.</p></article>';
    };
    const loadRecipes = async () => {
        const data = await getJson('/api/recipes.php');
        recipeStatus.textContent = `${data.items.length} recipe${data.items.length === 1 ? '' : 's'} shown.`;
        recipeList.innerHTML = data.items.length ? data.items.map(renderRecipe).join('') : '<article class="history-card empty-state"><p>Save a recipe above for repeat export and package choices.</p></article>';
    };
    const loadCollections = async () => {
        const data = await getJson('/api/collections.php');
        collectionSelect.innerHTML = '<option value="">No collection action</option>' + data.items.map((collection) => `<option value="${collection.id}">${escapeHtml(collection.name)}</option>`).join('');
    };
    void loadRules();
    void loadRecipes();
    void loadCollections();
    return cleanupAll([
        listen(form, 'submit', async (event) => { event.preventDefault(); await postJson('/api/auto-rules-save.php', Object.fromEntries(new FormData(form).entries())); form.reset(); await loadRules(); }),
        listen(preferencesForm, 'submit', async (event) => { event.preventDefault(); const data = formFilters(preferencesForm); data.default_export_types = new FormData(preferencesForm).getAll('default_export_types'); await postJson('/api/workspace-preferences.php', data); }),
        listen(recipeForm, 'submit', async (event) => { event.preventDefault(); const data = formFilters(recipeForm); data.preferred_export_types = new FormData(recipeForm).getAll('preferred_export_types'); await postJson('/api/recipes-save.php', data); recipeForm.reset(); await loadRecipes(); }),
        listen(document.querySelector('#refresh-rules'), 'click', loadRules),
        listen(document.querySelector('#refresh-recipes'), 'click', loadRecipes),
        listen(list, 'click', async (event) => { const toggle = event.target.closest('[data-toggle-id]'); const remove = event.target.closest('[data-delete-id]'); if (toggle) { await postJson('/api/auto-rules-toggle.php', { id: Number(toggle.dataset.toggleId), enabled: toggle.dataset.enabled !== 'true' }); await loadRules(); } if (remove) { await postJson('/api/auto-rules-delete.php', { id: Number(remove.dataset.deleteId) }); await loadRules(); } }),
        listen(recipeList, 'click', async (event) => { const remove = event.target.closest('[data-delete-recipe]'); if (remove) { await postJson('/api/recipes-delete.php', { id: Number(remove.dataset.deleteRecipe) }); await loadRecipes(); } }),
    ]);
}

function showWorkspaceResult(job, resultCard, resultContent, sourceUrl) {
    const meta = job.metadata || {};
    const result = job.result || {};
    resultCard.classList.add('is-ok');
    resultCard.classList.remove('is-error');
    resultContent.innerHTML = `<p>${escapeHtml(meta.title || result.file_name || 'Download ready')}</p>${job.duplicate_of_job_id ? `<p class="notice">Duplicate URL detected. Original job: ${escapeHtml(job.duplicate_of_job_id)}</p>` : ''}<button type="button" id="open-result-drawer">Open Result Details</button>`;
    document.querySelector('#open-result-drawer')?.addEventListener('click', (event) => {
        window.SaveFlowA11y?.openDrawer({
            title: 'Result Details',
            body: `<p>${escapeHtml(meta.title || result.file_name || 'Download ready')}</p><ul class="meta-list"><li><span>Platform:</span> ${escapeHtml(job.platform)}</li><li><span>Type:</span> ${escapeHtml(result.mime_type || 'Unknown')}</li><li><span>File:</span> ${escapeHtml(result.file_name || 'Ready file')}</li><li><span>Size:</span> ${formatBytes(result.size_bytes)}</li></ul>`,
            returnFocusTo: event.currentTarget,
            actions: [
                { label: 'Download', href: result.download_url || '#' },
                { label: 'Inspect', href: `/inspector.html?id=${encodeURIComponent(job.id)}` },
                { label: 'Open History', href: '/history.html' },
                { label: 'Copy URL', kind: 'secondary', onSelect: () => navigator.clipboard?.writeText(sourceUrl || meta.webpage_url || '') },
            ],
        });
    });
    document.querySelector('#open-result-drawer')?.click();
    window.SaveFlowA11y?.announce('Download ready.');
}

function showWorkspaceError(error, resultCard, resultContent, form, sourceUrl) {
    resultCard.classList.add('is-error');
    resultContent.innerHTML = `<p>${escapeHtml(error.message)}</p><button type="button" id="open-error-drawer">Open Error Details</button>`;
    document.querySelector('#open-error-drawer')?.addEventListener('click', (event) => {
        window.SaveFlowA11y?.openDrawer({
            title: 'Job Failed',
            body: `<p>${escapeHtml(error.message)}</p>`,
            returnFocusTo: event.currentTarget,
            actions: [
                ...(sourceUrl ? [{ label: 'Retry', onSelect: () => form.requestSubmit() }] : []),
                { label: 'Open History', href: '/history.html' },
            ],
        });
    });
    document.querySelector('#open-error-drawer')?.click();
    window.SaveFlowA11y?.announce(error.message);
}

function renderHistoryItem(item) {
    const title = item.title || item.file_name || item.normalized_url;
    const actions = [
        { label: item.is_favorite ? 'Unfavorite' : 'Favorite', selector: `[data-favorite-id="${item.id}"]` },
        { label: 'Save Note', selector: `[data-note-id="${item.id}"]` },
        ...(item.result?.download_url && item.status === 'completed' ? [{ label: 'Download', href: item.result.download_url }] : []),
        ...(item.status === 'completed' ? [{ label: 'Inspect', href: `/inspector.html?id=${item.id}` }] : []),
        { label: 'Add to Collection', selector: `[data-collection-id="${item.id}"]` },
        { label: 'Retry', selector: `[data-retry-id="${item.id}"]` },
    ];
    return `<article class="history-card"><div class="history-main">${item.thumbnail_url ? `<img src="${escapeAttribute(item.thumbnail_url)}" alt="" loading="lazy">` : '<div class="thumb-placeholder" aria-hidden="true"></div>'}<div><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(title)}</h3>${item.duplicate_of_job_id ? `<p class="notice">Duplicate of job ${escapeHtml(item.duplicate_of_job_id)}</p>` : ''}<ul class="meta-list"><li><span>Author:</span> ${escapeHtml(item.author || 'Unknown')}</li><li><span>Type:</span> ${escapeHtml(item.mime_type || 'Unknown')}</li><li><span>Size:</span> ${formatBytes(item.size_bytes)}</li></ul><label class="note-field">Note<textarea maxlength="500" data-note-for="${escapeAttribute(item.id)}">${escapeHtml(item.user_note || '')}</textarea></label></div></div><div class="history-actions"><button class="action-direct" type="button" data-favorite-id="${escapeAttribute(item.id)}" data-favorite="${item.is_favorite ? 'true' : 'false'}">${item.is_favorite ? 'Unfavorite' : 'Favorite'}</button><button class="action-direct" type="button" data-note-id="${escapeAttribute(item.id)}">Save Note</button>${item.result?.download_url && item.status === 'completed' ? `<span class="action-direct"><a class="button-link" href="${escapeAttribute(item.result.download_url)}">Download</a></span>` : ''}${item.status === 'completed' ? `<span class="action-direct"><a class="button-link secondary" href="/inspector.html?id=${escapeAttribute(item.id)}">Inspect</a></span>` : ''}<button class="action-direct" type="button" data-collection-id="${escapeAttribute(item.id)}">Add to Collection</button><button class="action-direct" type="button" data-retry-id="${escapeAttribute(item.id)}">Retry</button><button type="button" class="action-sheet-trigger" data-sheet-title="${escapeAttribute(title)}" data-sheet-actions="${escapeAttribute(JSON.stringify(actions))}">Actions</button></div></article>`;
}

function renderQueueItem(item) {
    const archiveLabel = item.status === 'archived' ? 'Unarchive' : 'Archive';
    const canProcess = item.status !== 'archived';
    const actions = [
        ...(canProcess ? [{ label: item.status === 'failed' ? 'Retry' : 'Process', selector: `[data-process-id="${item.id}"]` }] : []),
        { label: archiveLabel, selector: `[data-archive-id="${item.id}"]` },
        ...(item.job_id ? [{ label: 'Open History', href: '/history.html' }] : []),
    ];
    return `<article class="history-card ${canProcess ? 'queue-resumable' : ''}"><div><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(item.normalized_url)}</h3>${item.duplicate_of_job_id ? `<p class="notice">Duplicate of job ${escapeHtml(item.duplicate_of_job_id)}</p>` : ''}${item.note ? `<p>${escapeHtml(item.note)}</p>` : ''}${item.error ? `<p class="notice">${escapeHtml(item.error.message)}</p>` : ''}</div><div class="history-actions">${canProcess ? `<button class="action-direct" type="button" data-process-id="${item.id}">${item.status === 'failed' ? 'Retry' : 'Process'}</button>` : ''}<button class="action-direct" type="button" data-archive-id="${item.id}" data-archive="${item.status === 'archived' ? 'true' : 'false'}">${archiveLabel}</button>${item.job_id ? '<span class="action-direct"><a class="button-link secondary" href="/history.html">Open History</a></span>' : ''}<button type="button" class="action-sheet-trigger" data-sheet-title="${escapeAttribute(item.normalized_url)}" data-sheet-actions="${escapeAttribute(JSON.stringify(actions))}">Actions</button></div></article>`;
}

function renderVaultItem(item) {
    const archiveLabel = item.is_archived ? 'Unarchive' : 'Archive';
    const actions = [{ label: 'Move To Queue', selector: `[data-move-id="${item.id}"]` }, { label: archiveLabel, selector: `[data-archive-id="${item.id}"]` }];
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.platform)} ${item.is_archived ? '- archived' : ''}</p><h3>${escapeHtml(item.normalized_url)}</h3>${item.note ? `<p>${escapeHtml(item.note)}</p>` : ''}</div><div class="history-actions"><button class="action-direct" type="button" data-move-id="${item.id}">Move To Queue</button><button class="action-direct" type="button" data-archive-id="${item.id}" data-archive="${item.is_archived ? 'true' : 'false'}">${archiveLabel}</button><button type="button" class="action-sheet-trigger" data-sheet-title="${escapeAttribute(item.normalized_url)}" data-sheet-actions="${escapeAttribute(JSON.stringify(actions))}">Actions</button></div></article>`;
}

function renderAdminFailedJob(item) {
    const title = item.normalized_url || item.id;
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.platform)} - failed</p><h3>${escapeHtml(title)}</h3><p class="notice">${escapeHtml(item.error_code || 'JOB_FAILED')}</p><p>${escapeHtml(item.error_message || item.message || 'No error message recorded.')}</p><p class="hint">Updated: ${escapeHtml(item.updated_at || 'Unknown')}</p></div><div class="history-actions"><a class="button-link secondary" href="/history.html">Open History</a><a class="button-link secondary" href="/inspector.html?id=${escapeAttribute(item.id)}">Inspect</a></div></article>`;
}

function renderAdminQueueItem(item) {
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(item.normalized_url)}</h3>${item.error_message ? `<p class="notice">${escapeHtml(item.error_message)}</p>` : ''}<p class="hint">Created: ${escapeHtml(item.created_at || 'Unknown')}</p></div><div class="history-actions"><a class="button-link secondary" href="/queue.html">Open Queue</a>${item.job_id ? `<a class="button-link secondary" href="/inspector.html?id=${escapeAttribute(item.job_id)}">Inspect Job</a>` : ''}</div></article>`;
}

function renderAdminAuditEvent(item) {
    const actor = item.username || item.email || 'Unknown actor';
    return `<article class="history-card"><div><p class="eyebrow">${item.success ? 'Success' : 'Failed'} event</p><h3>${escapeHtml(item.action)}</h3><p>${escapeHtml(actor)}</p><p class="hint">${escapeHtml(item.created_at || 'Unknown')} ${item.ip_address ? `- ${escapeHtml(item.ip_address)}` : ''}</p></div></article>`;
}

function renderCollection(collection) {
    return `<article class="history-card"><div><p class="eyebrow">${collection.is_archived ? 'Archived' : 'Active'} collection</p><h3>${escapeHtml(collection.name)}</h3><p>${escapeHtml(collection.description || 'No description')}</p><p class="hint">${Number(collection.item_count || 0)} items</p></div><div class="history-actions"><button type="button" data-open-id="${collection.id}">Open</button><button type="button" data-edit-id="${collection.id}" data-name="${escapeAttribute(collection.name)}" data-description="${escapeAttribute(collection.description || '')}" data-color="${escapeAttribute(collection.color_label || '')}">Edit</button><button type="button" data-archive-id="${collection.id}" data-archived="${collection.is_archived ? 'true' : 'false'}">${collection.is_archived ? 'Restore' : 'Archive'}</button></div></article>`;
}

function renderCollectionItem(item) {
    const tags = item.tags?.length ? `<p class="notice">Tags: ${item.tags.map((tag) => `<button class="tag-button" type="button" data-filter-tag="${escapeAttribute(tag)}">${escapeHtml(tag)}</button>`).join(' ')}</p>` : '';
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(item.title || item.normalized_url)}</h3>${tags}<p>${escapeHtml(item.collection_note || item.user_note || '')}</p></div><div class="history-actions"><a class="button-link secondary" href="/inspector.html?id=${escapeAttribute(item.id)}">Inspect</a><button type="button" data-remove-job="${escapeAttribute(item.collection_item_id)}">Remove</button></div></article>`;
}

function renderArchiveExport(item) {
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.export_kind || 'Archive export')}</p><h3>${escapeHtml(item.file_name)}</h3><p class="hint">${formatBytes(item.size_bytes)} - ${escapeHtml(item.expires_at || '')}</p></div><div class="history-actions"><a class="button-link" href="${escapeAttribute(item.download_url)}">Download</a></div></article>`;
}

function renderResearchItem(item) {
    const tags = item.tags?.length ? `<p class="notice">Tags: ${item.tags.map((tag) => `<button class="tag-button" type="button" data-filter-tag="${escapeAttribute(tag)}">${escapeHtml(tag)}</button>`).join(' ')}</p>` : '';
    return `<article class="history-card research-card" data-research-id="${escapeAttribute(item.id)}"><div><label class="inline-check"><input type="checkbox" data-select-job value="${escapeAttribute(item.id)}"> Select</label><p class="eyebrow">${escapeHtml(item.platform)} - ${escapeHtml(item.status)}</p><h3>${escapeHtml(item.title || item.normalized_url)}</h3>${tags}<ul class="meta-list"><li><span>Author:</span> ${escapeHtml(item.author || 'Unknown')}</li><li><span>Size:</span> ${formatBytes(item.size_bytes)}</li></ul><label class="note-field">Job note<textarea maxlength="500" data-note-for="${escapeAttribute(item.id)}">${escapeHtml(item.user_note || '')}</textarea></label><button type="button" data-save-note="${escapeAttribute(item.id)}">Save Note</button></div><div class="history-actions"><a class="button-link secondary" href="/history.html">History</a><a class="button-link secondary" href="/inspector.html?id=${escapeAttribute(item.id)}">Inspect</a></div></article>`;
}

function renderDownload(item) {
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(item.item_kind || 'download')}</p><h3>${escapeHtml(item.source_label || item.file_name || 'File')}</h3><p class="hint">${formatBytes(item.size_bytes)} ${escapeHtml(item.mime_type || '')}</p></div><div class="history-actions"><a class="button-link" href="${escapeAttribute(item.download_url)}">Download</a><button type="button" class="action-sheet-trigger" data-sheet-title="${escapeAttribute(item.source_label || 'File actions')}" data-sheet-actions="${escapeAttribute(JSON.stringify([{ label: 'Download', href: item.download_url }]))}">Actions</button></div></article>`;
}

function renderRule(rule) {
    return `<article class="history-card"><div><p class="eyebrow">${rule.is_enabled ? 'Enabled' : 'Paused'}</p><h3>${escapeHtml(rule.name)}</h3><p class="hint">${escapeHtml(rule.match_platform || 'Any platform')} - ${escapeHtml(rule.match_status || 'Any status')}</p></div><div class="history-actions"><button type="button" data-toggle-id="${rule.id}" data-enabled="${rule.is_enabled ? 'true' : 'false'}">${rule.is_enabled ? 'Pause' : 'Enable'}</button><button type="button" data-delete-id="${rule.id}">Delete</button></div></article>`;
}

function renderRecipe(recipe) {
    return `<article class="history-card"><div><p class="eyebrow">${escapeHtml(recipe.platform || 'Any platform')}</p><h3>${escapeHtml(recipe.name)}</h3><p class="hint">${escapeHtml(recipe.preferred_package_type || 'No package preference')}</p></div><div class="history-actions"><button type="button" data-delete-recipe="${recipe.id}">Delete</button></div></article>`;
}

async function chooseCollectionForJob(jobId, button, statusText) {
    const data = await getJson('/api/collections.php');
    const active = (data.items || []).filter((collection) => !collection.is_archived);
    if (!active.length) {
        statusText.textContent = 'Create a collection first.';
        return;
    }
    window.SaveFlowA11y?.openActionSheet({
        title: 'Add to collection',
        returnFocusTo: button,
        actions: active.map((collection) => ({
            label: collection.name,
            onSelect: async () => {
                await postJson('/api/collections-add-job.php', { collection_id: collection.id, job_id: jobId });
                statusText.textContent = `Added to ${collection.name}.`;
            },
        })),
    });
}

function renderCollectionOptions(collections, ...targets) {
    const options = `<option value="">All collections</option>${collections.map((collection) => `<option value="${collection.id}">${escapeHtml(collection.name)}</option>`).join('')}`;
    targets.forEach((target, index) => {
        if (target) {
            target.innerHTML = index === 1 ? options.replace('All collections', 'Choose collection') : options;
        }
    });
}

function extractSharedUrl(search) {
    const params = new URLSearchParams(search);
    const candidates = [
        params.get('url'),
        params.get('text'),
        params.get('title'),
    ];

    for (const candidate of candidates) {
        const match = String(candidate || '').match(/https?:\/\/[^\s"'<>]+/i);
        if (match) {
            return match[0];
        }
    }

    return '';
}
